import type { ReactElement } from 'react';
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

import type { FormFieldError } from './types';

import { getFormFieldError } from './hooks/formErrors';

interface FormFieldRenderProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  error?: FormFieldError;
  field: ControllerRenderProps<TFieldValues, TName>;
}

interface FormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  render: (props: FormFieldRenderProps<TFieldValues, TName>) => ReactElement;
}

export function FormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ control, name, render }: FormFieldProps<TFieldValues, TName>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) =>
        render({
          error: getFormFieldError(fieldState.error),
          field,
        })
      }
    />
  );
}

FormField.displayName = 'FormField';
